import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/lib/permissions";
import { useProducts } from "@/lib/products";
import api, { apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import { PlugZap, ShieldCheck, Power, Unplug, AlertTriangle, Loader2, Link2, Activity } from "lucide-react";
import { sharedGet, visibleInterval } from "@/lib/poll";

const BROKERS = ["Zerodha", "Angel One", "Upstox", "Fyers"];

// Seconds -> "4s ago" / "3m ago". A feed that has never synced says so; it is never blank,
// because "no value" and "stale value" must not look the same.
function age(seconds) {
  if (seconds === null || seconds === undefined) return "never synced";
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

// A field Zerodha did not report renders as "—", never as ₹0: the difference between "no money"
// and "not reported" matters before a live order.
function Money({ value }) {
  return <span className="mono">{value === null || value === undefined ? "—" : inr(value)}</span>;
}

function State({ ok, label, detail }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-sm term-panel-2">
      <span className={`h-2.5 w-2.5 rounded-full ${ok ? "bg-[#00C805]" : "bg-[#475569]"}`} />
      <span className="text-xs text-slate-300">{label}</span>
      {detail && <span className="text-[10px] mono text-slate-500">{detail}</span>}
      <span className={`ml-auto text-[10px] mono ${ok ? "text-[#00C805]" : "text-slate-500"}`}>{ok ? "OK" : "—"}</span>
    </div>
  );
}

const AUTO_STATUS_CLS = {
  VERIFIED: "text-[#00C805]", ALREADY_VERIFIED: "text-[#00C805]",
  NOT_VERIFIED: "text-[#FF9F0A]", ERROR: "text-[#FF6b62]",
};
const AUTO_STATUS_TEXT = { ALREADY_VERIFIED: "VERIFIED (earlier)" };

/** Automatic post-login quantity verification for THIS user: every MCX underlying in the
 *  instrument master and every NIFTY 50 constituent with futures. Checking is not approval:
 *  only instruments that passed are LIVE-eligible. Polled; read-only. */
export function AutoQuantityVerification({ connected }) {
  const [view, setView] = useState(null);
  useEffect(() => {
    if (!connected) return undefined;
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get("/zerodha/quantity-verification"); if (alive) setView(data); }
      catch { /* shown as "no run yet" */ }
    };
    load();
    const stop = visibleInterval(load, 60000);
    return () => { alive = false; stop(); };
  }, [connected]);
  if (!connected) return null;
  const run = view?.last_run;
  const rows = view?.instruments || [];
  const nifty = run?.nifty50 || {};
  const c = run?.counts || {};
  return (
    <div className="space-y-2" data-testid="auto-quantity-verification">
      <div className="text-[11px] mono text-slate-400">
        {!run ? "Automatic verification runs after login — no run yet." : (
          <>
            Automatic ({run.trigger}) · {new Date(run.finished_at).toLocaleString()} ·{" "}
            <span className="text-[#00C805]">{(c.VERIFIED || 0) + (c.ALREADY_VERIFIED || 0)} verified</span> ·{" "}
            <span className="text-[#FF9F0A]">{c.NOT_VERIFIED || 0} not verified</span>
            {c.ERROR ? <span className="text-[#FF6b62]"> · {c.ERROR} error</span> : null}
            {" "}· unverified ones are retried every {Math.round((view.retry_interval_seconds || 1800) / 60)} min while their exchange is open
          </>
        )}
      </div>
      {run && (
        <div className="text-[11px] mono text-slate-500" data-testid="auto-quantity-nifty">
          NIFTY 50 list: {nifty.source || "—"}
          {nifty.as_of ? ` · as of ${new Date(nifty.as_of).toLocaleString()}` : ""}
          {nifty.error ? <span className="text-[#FF9F0A]"> · download failed: {nifty.error}</span> : null}
          {(nifty.not_in_fno_master || []).length ? ` · not in F&O: ${nifty.not_in_fno_master.join(", ")}` : ""}
        </div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-xs" data-testid="auto-quantity-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A]">
                <th className="text-left py-1.5 pr-3">Instrument</th>
                <th className="text-left py-1.5 pr-3">Status</th>
                <th className="text-left py-1.5">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.segment}:${r.underlying}`} className="gz-row" data-testid={`auto-q-${r.segment}-${r.underlying}`}>
                  <td className="py-1 pr-3 mono text-slate-300">{r.segment}:{r.underlying}</td>
                  <td className={`py-1 pr-3 mono font-bold ${AUTO_STATUS_CLS[r.status] || ""}`}>
                    {AUTO_STATUS_TEXT[r.status] || r.status}</td>
                  <td className="py-1 text-slate-400">
                    {[r.note === "CONTRACT_SIZE_UNKNOWN" ? "contract size unknown to GroundZero" : null,
                      ...(r.reasons || [])].filter(Boolean).join(" · ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function BrokerSession() {
  const { broker, refreshBroker, zerodha, systemStatus, refreshZerodha } = useApp();
  const { can } = useAuth();
  const canTrade = can(P.TRADING_VIEW);
  // The mock "Broker Connection" panel drives the development SIMULATION broker, whose state is
  // shared by the whole server — ADMIN only (simulator.use).
  const canSim = can(P.SIMULATOR);
  const nav = useNavigate();
  const [brokerName, setBrokerName] = useState("Zerodha");
  const [sec, setSec] = useState("connection");                // section shown (Phase 5)
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState("");
  const [paper, setPaper] = useState(null);
  const [diag, setDiag] = useState(null);
  const [funds, setFunds] = useState(null);
  const [check, setCheck] = useState(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const [qty, setQty] = useState(null);
  const [qtyBusy, setQtyBusy] = useState(false);
  const [risk, setRisk] = useState(null);
  const [riskBusy, setRiskBusy] = useState("");
  const [buffer, setBuffer] = useState("");
  const [bufferBusy, setBufferBusy] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconBusy, setReconBusy] = useState(false);
  const [ready, setReady] = useState(null);
  const [probe, setProbe] = useState({ exchange: "MCX", tradingsymbol: "", underlying: "", note: "", confirm: "" });
  const [probeRun, setProbeRun] = useState(null);
  const [probeBusy, setProbeBusy] = useState(false);
  const canLive = can(P.TRADING_LIVE);
  // Futures-only tools (the futures probe order, the futures quantity model, the paper engine panel)
  // are not shown to an account that trades Options only; Options has its own checks in Hedging.
  const productsNow = useProducts();
  const futuresOn = productsNow.futures;
  const optionsOn = productsNow.options;
  const [relCheck, setRelCheck] = useState(null);
  const [relBusy, setRelBusy] = useState(false);
  const st = broker?.states || {};
  const zStatus = zerodha?.status || "DISCONNECTED";
  const zConnected = zStatus === "CONNECTED";

  useEffect(() => {
    const load = () => {
      if (canTrade) api.get("/paper/trades").then((r) => setPaper(r.data)).catch(() => {});
      sharedGet("/zerodha/market-diagnostics").then((r) => setDiag(r.data)).catch(() => {});
      api.get("/zerodha/funds").then((r) => setFunds(r.data)).catch(() => {});
      if (canTrade) api.get("/risk/controls").then((r) => setRisk(r.data)).catch(() => {});
      if (canTrade) api.get("/reconciliation").then((r) => setRecon(r.data)).catch(() => {});
      if (canTrade) api.get("/live/readiness").then((r) => setReady(r.data?.readiness)).catch(() => {});
    };
    load(); return visibleInterval(load, 3000);
  }, [canTrade]);

  // LIVE-1 verification. Read-only: it reads funds, positions, orders, trades, one order's
  // history and fills, and prices a contract note for an already-executed order. It cannot
  // place, modify or cancel anything.
  const runAccountCheck = async () => {
    setCheckBusy(true);
    try { const { data } = await api.get("/zerodha/account-check"); setCheck(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setCheckBusy(false); }
  };

  const copyCheck = async () => {
    try { await navigator.clipboard.writeText(JSON.stringify(check, null, 2)); toast.success("Result copied"); }
    catch { toast.error("Could not copy — select the JSON below instead"); }
  };

  // LIVE-2 verification. Read-only: the only broker calls are the margin CALCULATORS, which
  // Zerodha documents as what-if pricing — no order is created and none can be.
  const runQuantityCheck = async () => {
    setQtyBusy(true);
    try { const { data } = await api.get("/zerodha/quantity-check"); setQty(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setQtyBusy(false); }
  };

  // LIVE-10. Both are read-only at the broker: reconciliation places, modifies and cancels
  // nothing — it only compares what we believe against what Zerodha says.
  const runReconciliation = async () => {
    setReconBusy(true);
    try {
      const { data } = await api.post("/reconciliation/run", {});
      setRecon((prev) => ({ ...(prev || {}), state: data.run.state, last_run: data.run }));
      const { data: view } = await api.get("/reconciliation");
      setRecon(view);
      if (view.state === "RECONCILED") toast.success("GroundZero and Zerodha agree");
      else toast.warning(`Reconciliation: ${view.state} — new entries are blocked`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setReconBusy(false); }
  };

  const resolveMismatch = async (id) => {
    const note = window.prompt("Why is this resolved? (recorded against your user)");
    if (!note || note.trim().length < 5) return;
    try {
      await api.post(`/reconciliation/mismatches/${id}/resolve`, { note: note.trim() });
      const { data } = await api.get("/reconciliation");
      setRecon(data);
      toast.success("Mismatch closed — it reopens if the next run still finds it");
    } catch (e) { toast.error(apiError(e)); }
  };

  // MARGIN SAFETY BUFFER (LIVE-12). The broker's required margin x this must still fit the
  // available funds. The floor of 1.0 is enforced by the server too — this is a convenience, not
  // the control.
  const saveBuffer = async () => {
    const value = Number(buffer);
    if (!Number.isFinite(value) || value < 1) {
      toast.error("The buffer cannot be below 1.0x — that would send orders the broker has "
                  + "already priced above the account's available funds.");
      return;
    }
    setBufferBusy(true);
    try {
      await api.post("/risk/limits", { limits: { margin_safety_multiple: value } });
      const { data } = await api.get("/risk/controls");
      setRisk(data); setBuffer("");
      toast[value < 1.5 ? "warning" : "success"](`Margin safety buffer set to ${value}x`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBufferBusy(false); }
  };

  const copyQty = async () => {
    try { await navigator.clipboard.writeText(JSON.stringify(qty, null, 2)); toast.success("Result copied"); }
    catch { toast.error("Could not copy — select the JSON below instead"); }
  };

  // Trading controls. STOP NEW ENTRIES and the KILL SWITCH stop ENTRIES only — exits, target
  // square-off, hedge recovery and reconciliation keep running, because a control that also
  // stopped those would leave an open position unmanaged. EXIT ALL additionally closes what is
  // open (and, in LIVE, can only report "flat" once Zerodha confirms it — LIVE-9).
  const setControl = async (path, enabled, label) => {
    setRiskBusy(path);
    try {
      await api.post(`/risk/${path}`, { enabled, reason: enabled ? "operator action" : null });
      const { data } = await api.get("/risk/controls");
      setRisk(data);
      toast[enabled ? "warning" : "success"](`${label} ${enabled ? "ON" : "OFF"}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRiskBusy(""); }
  };

  const exitAllAndStop = async () => {
    if (!window.confirm("EXIT ALL & STOP: stop new entries and square off every open trade?")) return;
    setRiskBusy("exit-all");
    try {
      // Confirmed above; the backend requires the confirmation explicitly as well.
      const { data } = await api.post("/paper/v25/trades/exit-all", { confirm: true, environment: "PAPER" });
      const pending = (data.trades || []).filter((t) => t.outcome === "EXIT_PENDING_RESUME").length;
      toast.warning(pending ? `Exit all: ${pending} trade(s) still closing — will resume`
        : `Exit all: ${data.trades.length} trade(s) handled`);
      const r = await api.get("/risk/controls");
      setRisk(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRiskBusy(""); }
  };

  // LIVE-7. This sends a REAL order to Zerodha, deliberately priced and sized so it cannot become
  // a position, and expects the broker to refuse it. The typed confirmation is required by the
  // backend too — the UI is not the safety control.
  const runProbe = async () => {
    if (!window.confirm("This sends a REAL order to Zerodha. It is priced below the market and "
      + "sized at one lot so it cannot fill, and it is expected to be REJECTED. Continue?")) return;
    setProbeBusy(true);
    try {
      const { data } = await api.post("/admin/live-probe/order", probe);
      setProbeRun(data);
      const outcome = data.run?.outcome;
      if (outcome === "REJECTED_BY_BROKER") toast.success("Probe rejected by Zerodha — as intended");
      else if (outcome === "REFUSED_BEFORE_SEND") toast.warning("Refused before sending — nothing was sent");
      else toast.error(`Probe outcome: ${outcome}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setProbeBusy(false); }
  };

  const runRelianceCheck = async () => {
    setRelBusy(true);
    try { const { data } = await api.get("/zerodha/reliance-check"); setRelCheck(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setRelBusy(false); }
  };

  const connectZerodha = async () => {
    setBusy("zerodha");
    try {
      const { data } = await api.post("/zerodha/auth-request");
      sessionStorage.setItem(`gz_auth_${data.auth_request_id}`,
        JSON.stringify({ auth_request_id: data.auth_request_id, secret: data.secret }));
      nav(data.connect_path);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const disconnectZerodha = async () => {
    try { await api.post("/zerodha/disconnect"); await refreshZerodha(); toast.success("Zerodha disconnected"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const act = async (fn, key, ok) => {
    setBusy(key);
    try { await fn(); await refreshBroker(); if (ok) toast.success(ok); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const connect = () => act(() => api.post("/broker/connect", { broker: brokerName, session_token: token }), "connect", "Broker connected");
  const validate = () => act(() => api.post("/broker/validate"), "validate", "Session valid");
  const disconnect = () => act(() => api.post("/broker/disconnect"), "disconnect", "Disconnected (positions preserved)");
  const exitAll = () => { if (window.confirm("Square off all strategies and stop automation?")) act(() => api.post("/broker/exit-all"), "exit", "Exit All & Stop done"); };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><PlugZap className="h-6 w-6 text-[#2563EB]" /> Broker Session</h1>
        <p className="text-sm text-slate-500 mt-1">Broker login, live market-data connection and system health. Tokens are masked and never stored in plain text.</p>
      </div>

      {/* UI/UX audit Phase 5: one page held the connection, risk controls, diagnostics and a REAL-order probe.
          Sections now; the probe has its own, and nothing below changed its behaviour. */}
      <div className="flex flex-wrap border-b border-[#2A2F3A]" role="tablist" data-testid="broker-sections">
        {[["connection", "Connection"], ...(canTrade ? [["controls", "Risk controls"]] : []), ["diagnostics", "Diagnostics"],
          ...(zConnected && canLive && futuresOn ? [["probe", "Real-order probe"]] : [])].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={sec === k} onClick={() => setSec(k)} data-testid={`broker-section-${k}`}
            className={`px-3 py-1.5 text-xs font-bold border-b-2 ${sec === k ? (k === "probe" ? "border-[#FF3B30] text-[#FF6b62]" : "border-[#2563EB] text-slate-100")
              : "border-transparent text-slate-500 hover:text-slate-200"}`}>{l}</button>))}
      </div>

      {sec === "diagnostics" && canSim && <>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">Simulation Broker (admin / dev)</div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Connect panel */}
        <div className="lg:col-span-2 term-panel rounded-sm p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[11px] text-slate-400 uppercase tracking-wide">Broker</label>
              <select data-testid="broker-select" value={brokerName} onChange={(e) => setBrokerName(e.target.value)} disabled={broker?.connected}
                className="w-full bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-3 py-2 text-sm outline-none focus:border-[#2563EB] disabled:opacity-60">
                {BROKERS.map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] text-slate-400 uppercase tracking-wide">Session Token</label>
              <input data-testid="session-token-input" type="password" value={token} onChange={(e) => setToken(e.target.value)}
                placeholder="paper-token-123" disabled={broker?.connected}
                className="w-full bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-3 py-2 text-sm mono outline-none focus:border-[#2563EB] disabled:opacity-60" />
            </div>
          </div>
          {broker?.connected && (
            <div className="text-xs text-slate-400 mono">Active token: <span className="text-[#00C805]">{broker.token_masked}</span> · expires {new Date(broker.session_expires_at).toLocaleTimeString()}</div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={connect} disabled={broker?.connected || busy} data-testid="connect-broker-btn"
              className="gz-btn gz-btn-primary">
              {busy === "connect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Connect Broker
            </button>
            <button onClick={validate} disabled={!broker?.connected || busy} data-testid="validate-session-btn"
              className="gz-btn gz-btn-secondary">
              <ShieldCheck className="h-4 w-4 text-[#00C805]" /> Validate Session
            </button>
            <button onClick={disconnect} disabled={!broker?.connected || busy} data-testid="disconnect-broker-btn"
              className="gz-btn gz-btn-secondary">
              <Unplug className="h-4 w-4 text-[#FFCC00]" /> Disconnect
            </button>
            <button onClick={exitAll} disabled={busy} data-testid="exit-all-btn"
              className="gz-btn gz-btn-danger">
              <Power className="h-4 w-4" /> EXIT ALL & STOP
            </button>
          </div>
          <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-[#1A1D24] border border-[#2A2F3A] rounded-sm p-3">
            <AlertTriangle className="h-4 w-4 text-[#FFCC00] shrink-0 mt-0.5" />
            Disconnecting does NOT close open positions. Use EXIT ALL & STOP to square off and halt automation. New orders auto-disable on token expiry.
          </div>
        </div>

        {/* Connection states */}
        <div className="term-panel rounded-sm p-5 space-y-2">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Simulation broker states</div>
          <div className="text-[10px] text-slate-500 -mt-1 mb-1" data-testid="sim-states-note">The mock broker above — not Zerodha. Zerodha's state is under Market Data Connection and Reconciliation below.</div>
          <State ok={st.broker_connected} label="Broker connected" />
          <State ok={st.market_data_connected} label="Market data connected" />
          <State ok={st.order_stream_connected} label="Order stream connected" />
          <State ok={st.positions_synced} label="Positions synchronized" />
          <State ok={st.funds_synced} label="Funds synchronized" />
          <State ok={st.automation_ready} label="Automation ready" />
        </div>
      </div>

      {/* Reconciliation */}
      {broker?.connected && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["Available Funds", inr(broker.free_margin)], ["Used Margin", inr(broker.used_margin)],
            ["Total Funds", inr(broker.total_funds)], ["Automation", broker.states.automation_ready ? "READY" : "STOPPED"]].map(([k, v]) => (
            <div key={k} className="term-panel rounded-sm p-4">
              <div className="text-[11px] text-slate-500 uppercase tracking-wide">{k}</div>
              <div className="text-lg font-semibold mono mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}
      </>}

      {sec === "connection" && <>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">Market Data Connection</div>

      {/* Zerodha (REAL market data) connect — extension nonce flow */}
      <div className="term-panel rounded-sm p-5 space-y-4" data-testid="zerodha-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-sm font-bold flex items-center gap-2"><Link2 className="h-4 w-4 text-[#2563EB]" /> Connect Zerodha (Live Market Data)</div>
            <div className="text-xs text-slate-500 mt-1">Real Kite session via the GroundZero Chrome extension. You never type or paste the enctoken.</div>
          </div>
          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-sm ${zConnected ? "bg-[#00C805]/15 text-[#00C805]" : zStatus === "EXPIRED" || zStatus === "ERROR" ? "bg-[#FF3B30]/15 text-[#FF6b62]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
            {zConnected ? `ZERODHA CONNECTED · ${zerodha.broker_user_id || ""}` : `ZERODHA ${zStatus}`}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {!zConnected ? (
            <button onClick={connectZerodha} disabled={busy === "zerodha"} data-testid="connect-zerodha-btn"
              className="gz-btn gz-btn-primary">
              {busy === "zerodha" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} CONNECT ZERODHA
            </button>
          ) : (
            <button onClick={disconnectZerodha} data-testid="disconnect-zerodha-btn"
              className="gz-btn gz-btn-secondary">
              <Unplug className="h-4 w-4 text-[#FFCC00]" /> Disconnect Zerodha
            </button>
          )}
          {(zStatus === "EXPIRED" || zStatus === "ERROR") && (
            <button onClick={connectZerodha} data-testid="reconnect-zerodha-btn"
              className="gz-btn gz-btn-danger">
              RECONNECT — session {zStatus.toLowerCase()}
            </button>
          )}
        </div>
      </div>
      </>}

      {/* REAL Zerodha account funds — the connected user's own money, as Zerodha reports it.
          Nothing here is derived by GroundZero, and nothing is simulated: PAPER capital lives on
          each paper trade instead. MCX is governed by the commodity segment, NFO/NSE by equity. */}
      {sec === "connection" && zConnected && (
        <>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">
            Zerodha Account Funds
          </div>
          <div className="term-panel rounded-sm p-5 space-y-4" data-testid="zerodha-funds-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-slate-500">
                Live figures for {zerodha?.broker_user_id || "the connected account"} — read from Zerodha, never calculated here.
              </div>
              <span data-testid="funds-sync-badge"
                className={`text-[10px] font-bold px-2 py-1 rounded-sm mono ${funds?.sync && !funds.sync.stale
                  ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
                {funds?.sync && !funds.sync.stale ? `SYNCED · ${age(funds.sync.age_seconds)}`
                  : `STALE · ${age(funds?.sync?.age_seconds)}`}
              </span>
            </div>
            {funds?.sync?.error && (
              <div className="text-[11px] mono text-[#FF6b62]">Last sync error: {funds.sync.error}</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[["commodity", "COMMODITY · MCX"], ["equity", "EQUITY · NFO / NSE"]].map(([key, label]) => {
                const seg = funds?.segments?.[key];
                return (
                  <div key={key} className="term-panel-2 rounded-sm p-4 space-y-2" data-testid={`funds-${key}`}>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
                    {!seg ? <div className="text-xs text-slate-600">Not reported for this account.</div> : (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        {[["Available margin (net)", seg.available_margin], ["Cash", seg.available_cash],
                          ["Live balance", seg.live_balance], ["Used margin", seg.used_margin],
                          ["SPAN", seg.span], ["Exposure", seg.exposure],
                          ["M2M unrealised", seg.m2m_unrealised], ["Collateral", seg.collateral]].map(([k, v]) => (
                          <React.Fragment key={k}>
                            <span className="text-slate-500">{k}</span>
                            <span className="text-right"><Money value={v} /></span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* LIVE-1 broker read-path verification. Proves, on the real account, that this enctoken
          session serves every endpoint the LIVE order lifecycle will depend on — before any
          order is ever written. Nothing here can change the account. */}
      {sec === "diagnostics" && zConnected && (
        <>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">
            Broker Read-Path Check (read-only)
          </div>
          <div className="term-panel rounded-sm p-5 space-y-4" data-testid="account-check-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-slate-500 max-w-2xl">
                Calls every read-only account endpoint on your own session: funds, positions, orders, trades,
                one order&apos;s history and its fills, and a virtual contract note. It places, modifies and
                cancels nothing.
              </div>
              <div className="flex gap-2">
                {check && (
                  <button onClick={copyCheck} data-testid="copy-account-check-btn"
                    className="px-3 py-2 rounded-sm text-xs font-semibold bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]">
                    Copy result
                  </button>
                )}
                <button onClick={runAccountCheck} disabled={checkBusy} data-testid="run-account-check-btn"
                  className="px-4 py-2 rounded-sm text-sm font-semibold bg-[#2563EB] hover:bg-[#1d4fd0] disabled:opacity-50 flex items-center gap-2">
                  {checkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  RUN ACCOUNT CHECK
                </button>
              </div>
            </div>

            {check && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] mono">
                  {[["Account", check.broker_user_id], ["Auth", check.auth_model],
                    ["Checked", check.checked_at ? new Date(check.checked_at).toLocaleTimeString() : "—"],
                    ["Live trading", check.live_trading_enabled ? "ON" : "OFF"]].map(([k, v]) => (
                    <div key={k} className="term-panel-2 rounded-sm px-3 py-2">
                      <div className="text-slate-500 text-[10px] uppercase tracking-wide">{k}</div>
                      <div className="text-slate-200 truncate">{v || "—"}</div>
                    </div>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="account-check-table">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A]">
                        <th className="text-left py-2 pr-3">Check</th>
                        <th className="text-left py-2 pr-3">Endpoint</th>
                        <th className="text-left py-2 pr-3">Result</th>
                        <th className="text-right py-2 pr-3">Records</th>
                        <th className="text-right py-2 pr-3">ms</th>
                        <th className="text-left py-2">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(check.checks || []).map((c) => (
                        <tr key={c.check} className="gz-row">
                          <td className="py-2 pr-3 mono text-slate-300">{c.check}</td>
                          <td className="py-2 pr-3 mono text-slate-500">{c.endpoint}</td>
                          <td className="py-2 pr-3">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${c.ok === true
                              ? "bg-[#00C805]/15 text-[#00C805]" : c.ok === false
                                ? "bg-[#FF3B30]/15 text-[#FF6b62]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
                              {c.ok === true ? "PASS" : c.ok === false ? "FAIL" : "SKIPPED"}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-right mono text-slate-400">
                            {c.record_count === null || c.record_count === undefined ? "—" : c.record_count}
                          </td>
                          <td className="py-2 pr-3 text-right mono text-slate-500">{c.latency_ms ?? "—"}</td>
                          <td className="py-2 text-slate-500">
                            {c.message || c.note || (c.fields ? `fields: ${c.fields.slice(0, 6).join(", ")}${c.fields.length > 6 ? " …" : ""}` : "—")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-wrap gap-3 text-[11px] mono text-slate-500">
                  <span>funds segments: {(check.funds_segments || []).join(", ") || "none"}</span>
                  {check.account_feeds && Object.entries(check.account_feeds).map(([f, st]) => (
                    <span key={f} className={st.stale ? "text-[#FFCC00]" : "text-[#00C805]"}>
                      {f}: {age(st.age_seconds)}{st.stale ? " (STALE)" : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Trading controls (LIVE-4). These act on PAPER and LIVE trading through the risk gate —
          unlike the Risk Centre page, which drives the legacy simulator. */}
      {sec === "controls" && canTrade && (
        <>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">
            Trading Controls
          </div>
          <div className="term-panel rounded-sm p-5 space-y-4" data-testid="risk-controls-panel">
            {(risk?.state?.kill_switch || risk?.state?.stop_new_entries) && (
              <div className="text-xs px-3 py-2 rounded-sm bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF6b62]"
                data-testid="risk-banner">
                {risk.state.kill_switch ? "KILL SWITCH ACTIVE" : "NEW ENTRIES STOPPED"}
                {risk.state.global_kill_switch && " (set globally by an administrator)"}
                {risk.state.halted_reason ? ` — ${risk.state.halted_reason}` : ""}
                <span className="text-slate-400"> · exits, target square-off, hedge recovery and
                  reconciliation continue to run.</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setControl("stop-new-entries", !risk?.state?.stop_new_entries, "Stop new entries")}
                disabled={!!riskBusy} data-testid="stop-entries-btn"
                className={`px-4 py-2 rounded-sm text-sm font-semibold border flex items-center gap-2 ${risk?.state?.stop_new_entries
                  ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/40"
                  : "bg-[#1A1D24] border-[#2A2F3A] hover:bg-[#232730]"}`}>
                {riskBusy === "stop-new-entries" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
                {risk?.state?.stop_new_entries ? "RESUME ENTRIES" : "STOP NEW ENTRIES"}
              </button>
              <button onClick={() => setControl("kill-switch", !risk?.state?.kill_switch, "Kill switch")}
                disabled={!!riskBusy} data-testid="kill-switch-btn"
                className={`px-4 py-2 rounded-sm text-sm font-semibold border flex items-center gap-2 ${risk?.state?.kill_switch
                  ? "bg-[#FF3B30]/20 text-[#FF6b62] border-[#FF3B30]/50"
                  : "bg-[#1A1D24] border-[#2A2F3A] hover:bg-[#232730]"}`}>
                {riskBusy === "kill-switch" ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                {risk?.state?.kill_switch ? "CLEAR KILL SWITCH" : "KILL SWITCH"}
              </button>
              <button onClick={exitAllAndStop} disabled={!!riskBusy} data-testid="exit-all-btn"
                className="px-4 py-2 rounded-sm text-sm font-semibold bg-[#FF3B30]/15 text-[#FF6b62] border border-[#FF3B30]/40 flex items-center gap-2">
                {riskBusy === "exit-all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />}
                EXIT ALL &amp; STOP
              </button>
            </div>
            <div className="text-[11px] text-slate-500">
              STOP and KILL block new entries only. Exits, target square-off, hedge recovery and
              reconciliation keep running — closing a position is never blocked by a risk control.
            </div>
            {/* LIVE-12: the buffer an operator actually tunes. */}
            {risk?.limits && (
              <div className="term-panel-2 rounded-sm p-3 space-y-2" data-testid="margin-buffer">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">
                    Margin Safety Buffer
                  </span>
                  <span className="mono text-[13px] font-bold text-fg" data-testid="margin-buffer-value">
                    {risk.limits.margin_safety_multiple}x
                  </span>
                  {Number(risk.limits.margin_safety_multiple) < 1.5 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-bold bg-[#FFCC00]/15 text-[#FFCC00]">
                      BELOW RECOMMENDED
                    </span>
                  )}
                  <input value={buffer} onChange={(e) => setBuffer(e.target.value)}
                    type="number" step="0.05" min="1" placeholder="1.50"
                    data-testid="margin-buffer-input"
                    className="ml-auto w-24 bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-1 mono text-[12px]" />
                  <button onClick={saveBuffer} disabled={bufferBusy || !buffer}
                    data-testid="margin-buffer-save"
                    className="px-3 py-1.5 rounded-sm text-xs font-semibold bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730] disabled:opacity-40">
                    {bufferBusy ? "SAVING…" : "SET"}
                  </button>
                </div>
                <div className="text-[11px] text-slate-400">
                  The broker&apos;s required margin multiplied by this must still fit your available funds.
                  <span className="text-[#00C805] font-semibold"> 1.5x–2.0x is recommended for normal LIVE trading.</span>
                </div>
                <div className="text-[10px] text-[#FFCC00]">
                  ⚠ 1.0x means no buffer at all — the order is sent against the exact figure the broker quoted
                  moments ago, leaving very little room for RMS or intraday margin movement. Minimum allowed is 1.0x.
                </div>
              </div>
            )}
            {risk?.limits && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] mono" data-testid="risk-limits">
                {[...(futuresOn ? [["Futures max lots/order", risk.limits.max_lots_per_order],
                  ["Futures max broker qty/order", risk.limits.max_broker_quantity_per_order],
                  ["Futures allowlist", (risk.limits.instrument_allowlist || []).join(", ") || "none"],
                  ["Entry budget today", `${risk.budget?.entries_used ?? 0} / ${risk.budget?.entry_budget ?? 0}`]] : []),
                  ["Margin buffer", `${risk.limits.margin_safety_multiple}x`],
                  ...(optionsOn ? [["Options instruments", risk.limits.options_instrument_access === "SELECTED"
                    ? ((risk.limits.options_allowlist || []).join(", ") || "none selected") : "All NFO stock options"],
                  ["Options lots/order · pairs/trade", `${risk.limits.options_max_lots_per_order ?? 1} · ${risk.limits.options_max_pairs_per_trade ?? 1}`],
                  ["Options open trades max", risk.limits.options_max_open_trades ?? 1],
                  ["Options daily loss limit", Number(risk.limits.options_daily_loss_limit) ? inr(risk.limits.options_daily_loss_limit) : "not set"]] : [])].map(([k, v]) => (
                  <div key={k} className="term-panel-2 rounded-sm px-3 py-2">
                    <div className="text-slate-500 text-[10px] uppercase tracking-wide">{k}</div>
                    <div className="text-slate-200 truncate">{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* LIVE-10. Reconciliation and the readiness composite. The state shown here is read from
          the same place the risk gate reads, so the screen and the gate can never disagree. */}
      {sec === "controls" && canTrade && (
        <>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">
            Reconciliation &amp; Live Readiness
          </div>
          <div className="term-panel rounded-sm p-5 space-y-4" data-testid="reconciliation-panel">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`text-[11px] px-2 py-1 rounded-sm font-bold ${
                recon?.state === "RECONCILED" ? "bg-[#00C805]/15 text-[#00C805]"
                  : recon?.state === "MISMATCH" ? "bg-[#FF3B30]/20 text-[#FF6b62]"
                  : "bg-[#FFCC00]/15 text-[#FFCC00]"}`} data-testid="recon-state">
                {recon?.state || "UNKNOWN"}
              </span>
              {recon?.entries_blocked && (
                <span className="text-[11px] text-[#FF6b62] font-semibold">
                  NEW ENTRIES BLOCKED for this account — exits, square-off and monitoring continue
                </span>
              )}
              <span className="text-[11px] text-slate-500 mono">
                last run {recon?.last_run?.finished_at ? new Date(recon.last_run.finished_at).toLocaleTimeString() : "never"}
                {recon?.interval_seconds ? ` · every ${recon.interval_seconds}s` : ""}
              </span>
              <button onClick={runReconciliation} disabled={reconBusy || !zConnected}
                data-testid="recon-run-btn"
                className="ml-auto px-3 py-1.5 rounded-sm text-xs font-semibold bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730] flex items-center gap-2">
                {reconBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                RECONCILE NOW
              </button>
            </div>

            {/* the composite: every component named, an unknown one counted as not ready */}
            {!futuresOn && <div className="text-[11px] text-slate-500" data-testid="options-readiness-pointer">
              Options trading readiness (account keys, limits, funds, feed) is shown in Options Trading.</div>}
            {futuresOn && <div className="flex flex-wrap gap-2" data-testid="readiness-composite">
              {ready && Object.entries(ready.components || {}).map(([name, ok]) => (
                <span key={name}
                  className={`text-[10px] px-2 py-1 rounded-sm font-bold uppercase tracking-wide ${
                    ok ? "bg-[#00C805]/10 text-[#00C805]" : "bg-[#2A2F3A] text-slate-400"}`}>
                  {ok ? "✓" : "✗"} {name.replace(/_/g, " ")}
                </span>
              ))}
              {ready && (
                <span className={`text-[10px] px-2 py-1 rounded-sm font-bold uppercase tracking-wide ${
                  ready.live_execution_ready ? "bg-[#00C805]/20 text-[#00C805]" : "bg-[#FF3B30]/15 text-[#FF6b62]"}`}
                  data-testid="live-execution-ready">
                  {ready.live_execution_ready ? "FUTURES LIVE READY" : "FUTURES NOT READY FOR LIVE"}
                </span>
              )}
            </div>}

            {recon?.last_run?.unreadable?.length > 0 && (
              <div className="text-[11px] text-[#FFCC00] mono">
                could not be checked: {recon.last_run.unreadable.join(", ")} — not knowing is not agreeing,
                so entries stay blocked
              </div>
            )}

            {(recon?.open_mismatches || []).length > 0 ? (
              <div className="space-y-1" data-testid="recon-mismatches">
                {recon.open_mismatches.map((mm) => (
                  <div key={mm.id} className="term-panel-2 rounded-sm px-3 py-2 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="mono text-[11px] text-[#FF6b62] font-bold">{mm.kind}</div>
                      <div className="mono text-[11px] text-slate-300 truncate">{mm.subject}</div>
                      <div className="mono text-[10px] text-slate-500 truncate">{JSON.stringify(mm.detail)}</div>
                      <div className="text-[10px] text-slate-600">
                        seen in {mm.runs_seen} run(s) since {mm.first_seen_at ? new Date(mm.first_seen_at).toLocaleString() : "—"}
                      </div>
                    </div>
                    <button onClick={() => resolveMismatch(mm.id)}
                      className="px-2 py-1 rounded-sm text-[10px] font-semibold bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]">
                      RESOLVE…
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[11px] text-slate-500">
                No open mismatches. Orders are matched by our own tag, fills by the broker&apos;s trade id, and the
                net position per contract against what our fills add up to.
              </div>
            )}
          </div>
        </>
      )}

      {/* LIVE-7 broker-path probe. The ONE screen in the app that sends a real order on purpose. */}
      {sec === "probe" && zConnected && canLive && futuresOn && (
        <>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">
            Broker-Path Probe (sends a REAL order)
          </div>
          <div className="term-panel rounded-sm p-5 space-y-4 border border-[#FF3B30]/30" data-testid="live-probe-panel">
            <div className="text-xs text-slate-400 max-w-3xl">
              Sends <span className="text-[#FF6b62] font-bold">one real order</span> to Zerodha, expecting it to be
              <span className="font-bold"> REJECTED</span>. It cannot become a position: BUY only, LIMIT priced below
              the last trade, exactly one verified lot, and the broker&apos;s own margin calculator must say the order
              needs far more margin than the account has — otherwise it is refused before anything is sent. If it is
              accepted anyway it is cancelled within seconds and recorded FAILED&nbsp;SAFE.
              {!risk?.live_trading_enabled && (
                <span className="block mt-1 text-[#FFCC00]">
                  LIVE_TRADING_ENABLED is OFF — the probe will be refused before sending. Nothing will reach Zerodha.
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {[["exchange", "Exchange", "MCX"], ["tradingsymbol", "Trading symbol", "ZINC26OCTFUT"],
                ["underlying", "Underlying", "ZINC"], ["note", "Note (for the record)", "LIVE-7 run 1"],
                ["confirm", "Type: SEND REAL ORDER", "SEND REAL ORDER"]].map(([key, label, ph]) => (
                <div key={key} className="space-y-1">
                  <label className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</label>
                  <input value={probe[key] || ""} placeholder={ph} data-testid={`probe-${key}`}
                    onChange={(e) => setProbe({ ...probe, [key]: e.target.value })}
                    className="w-full bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2 py-1.5 text-xs mono outline-none focus:border-[#2563EB]" />
                </div>
              ))}
            </div>
            <button onClick={runProbe} disabled={probeBusy || !probe.tradingsymbol || !probe.confirm}
              data-testid="run-probe-btn"
              className="px-4 py-2 rounded-sm text-sm font-semibold bg-[#FF3B30]/15 text-[#FF6b62] border border-[#FF3B30]/50 disabled:opacity-40 flex items-center gap-2">
              {probeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
              SEND PROBE ORDER
            </button>

            {probeRun && (
              <div className="space-y-3" data-testid="probe-result">
                <div className={`text-xs font-bold px-3 py-2 rounded-sm ${probeRun.run?.outcome === "REJECTED_BY_BROKER"
                  ? "bg-[#00C805]/10 text-[#00C805] border border-[#00C805]/40"
                  : probeRun.run?.outcome === "REFUSED_BEFORE_SEND"
                    ? "bg-[#FFCC00]/10 text-[#FFCC00] border border-[#FFCC00]/40"
                    : "bg-[#FF3B30]/10 text-[#FF6b62] border border-[#FF3B30]/40"}`}>
                  {probeRun.run?.outcome}
                  {probeRun.run?.reasons?.length ? ` — ${probeRun.run.reasons.join(" · ")}` : ""}
                </div>
                {/* The field-by-field comparison the team checks against the Kite order book. */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="probe-comparison">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A]">
                        <th className="text-left py-2 pr-3">Field</th>
                        <th className="text-left py-2 pr-3">GroundZero intended / captured</th>
                        <th className="text-left py-2">Kite (check by hand)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(probeRun.comparison?.fields || []).map((f) => (
                        <tr key={f.field} className="gz-row">
                          <td className="py-1.5 pr-3 text-slate-400">{f.field}</td>
                          <td className="py-1.5 pr-3 mono text-slate-200">{String(f.groundzero ?? "—")}</td>
                          <td className="py-1.5 text-slate-600">☐</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] mono">
                  {Object.entries(probeRun.comparison?.checks || {}).map(([k, v]) => (
                    <div key={k} className={`term-panel-2 rounded-sm px-2 py-1.5 ${v === true ? "text-[#00C805]"
                      : v === false ? "text-[#FF6b62]" : "text-slate-400"}`}>
                      {v === true ? "✓" : v === false ? "✗" : "·"} {k}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* LIVE-2 quantity model. Proves, per instrument, what Zerodha `quantity` ONE strategy lot
          is — the difference between 1 ZINC contract and 5000 of them. Read-only. */}
      {sec === "diagnostics" && zConnected && futuresOn && (
        <>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">
            Quantity Model Verification (read-only)
          </div>
          <div className="term-panel rounded-sm p-5 space-y-4" data-testid="quantity-check-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-slate-500 max-w-2xl">
                Cross-checks the instrument master, the Excel multiplier, what PAPER uses today, Zerodha&apos;s
                margin calculator at the proposed quantity and at twice it, any stored broker probe and any
                real order ever sent. Uses the margin calculator only — it creates no order. Runs
                automatically after every Zerodha login; the button below is an optional re-check of
                the original five instruments.
              </div>
              <div className="flex gap-2">
                {qty && (
                  <button onClick={copyQty} data-testid="copy-quantity-check-btn"
                    className="px-3 py-2 rounded-sm text-xs font-semibold bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]">
                    Copy result
                  </button>
                )}
                <button onClick={runQuantityCheck} disabled={qtyBusy} data-testid="run-quantity-check-btn"
                  className="px-4 py-2 rounded-sm text-sm font-semibold bg-[#2563EB] hover:bg-[#1d4fd0] disabled:opacity-50 flex items-center gap-2">
                  {qtyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  VERIFY QUANTITY MODEL
                </button>
              </div>
            </div>

            <AutoQuantityVerification connected={zConnected} />

            {qty && (
              <div className="space-y-3">
                <div className="text-[11px] mono text-slate-400">
                  {qty.verified}/{qty.checked} verified · account {qty.broker_user_id || "—"} ·
                  live trading {qty.live_trading_enabled ? "ON" : "OFF"}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="quantity-check-table">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A]">
                        <th className="text-left py-2 pr-3">Instrument</th>
                        <th className="text-right py-2 pr-3">Lots</th>
                        <th className="text-right py-2 pr-3">Excel lot</th>
                        <th className="text-right py-2 pr-3">Master lot_size</th>
                        <th className="text-right py-2 pr-3">Broker qty</th>
                        <th className="text-right py-2 pr-3">P&amp;L mult.</th>
                        <th className="text-right py-2 pr-3">LTP</th>
                        <th className="text-right py-2 pr-3">Margin</th>
                        <th className="text-right py-2 pr-3">% notional</th>
                        <th className="text-left py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(qty.instruments || []).map((r) => {
                        const mo = (r.sources && r.sources.margins_orders) || {};
                        return (
                          <tr key={`${r.segment}:${r.underlying}`} className="gz-row">
                            <td className="py-2 pr-3 mono text-slate-300">{r.segment}:{r.underlying}</td>
                            <td className="py-2 pr-3 text-right mono">{r.strategy_lots_per_pair ?? "—"}</td>
                            <td className="py-2 pr-3 text-right mono text-slate-400">
                              {(r.sources && r.sources.excel_data_misc && r.sources.excel_data_misc.lot) ?? "—"}</td>
                            <td className="py-2 pr-3 text-right mono text-slate-400">{r.broker_lot_size ?? "—"}</td>
                            <td className="py-2 pr-3 text-right mono text-[#00C805]">{r.proposed_broker_quantity ?? "—"}</td>
                            <td className="py-2 pr-3 text-right mono text-slate-400">{r.pnl_multiplier ?? "—"}</td>
                            <td className="py-2 pr-3 text-right mono text-slate-400"
                              title={mo.ltp_source === "REST_LTP" ? "GET /quote/ltp (read-only fallback)"
                                : mo.ltp_source === "WS_TICK" ? "live WebSocket tick" : "no price"}>
                              {mo.ltp ?? "—"}
                              <span className="text-[9px] text-slate-600 ml-1">
                                {mo.ltp_source === "REST_LTP" ? "REST" : mo.ltp_source === "WS_TICK" ? "WS" : ""}
                              </span>
                            </td>
                            <td className="py-2 pr-3 text-right mono text-slate-400">
                              {mo.margin_at_proposed ? inr(mo.margin_at_proposed) : "—"}</td>
                            <td className="py-2 pr-3 text-right mono text-slate-400">
                              {mo.margin_pct_of_notional ? `${mo.margin_pct_of_notional}%` : "—"}</td>
                            <td className="py-2">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${r.status === "VERIFIED"
                                ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FF3B30]/15 text-[#FF6b62]"}`}>
                                {r.status === "VERIFIED" ? "VERIFIED" : "NOT VERIFIED"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {(qty.instruments || []).filter((r) => r.reasons && r.reasons.length > 0).map((r) => (
                  <div key={`why-${r.segment}:${r.underlying}`} className="text-[11px] text-[#FF6b62]">
                    {r.segment}:{r.underlying} — {r.reasons.join(" · ")}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {sec === "connection" && <div className="text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-[#2A2F3A] pb-1">System Status</div>}

      {/* System status — truthful, derived from real service state */}
      {sec === "connection" && systemStatus && (
        <div className="term-panel rounded-sm p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-2"><Activity className="h-4 w-4" /> System Status</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="system-status">
            <State ok={systemStatus.groundzero_backend} label="GroundZero Backend" />
            <State ok={systemStatus.zerodha_session === "CONNECTED"} label={`Zerodha: ${systemStatus.zerodha_session}`} />
            <State ok={systemStatus.zerodha_market_ws} label="Market WebSocket" />
            <State ok={systemStatus.instrument_sync} label="Instrument Sync" />
            <State ok={systemStatus.order_sync} label="Order Sync"
              detail={systemStatus.account_feeds && age(systemStatus.account_feeds.orders.age_seconds)} />
            <State ok={systemStatus.position_sync} label="Position Sync"
              detail={systemStatus.account_feeds && age(systemStatus.account_feeds.positions.age_seconds)} />
            <State ok={systemStatus.funds_sync} label="Funds Sync"
              detail={systemStatus.account_feeds && age(systemStatus.account_feeds.funds.age_seconds)} />
            <State ok={systemStatus.live_trading_enabled} label={`Live Trading: ${systemStatus.live_trading_enabled ? "ON" : "OFF"}`} />
          </div>
        </div>
      )}

      {/* Market Data Diagnostics — real WebSocket transport */}
      {sec === "diagnostics" && diag && (
        <div className="term-panel rounded-sm p-5" data-testid="market-diagnostics">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400 flex items-center gap-2"><Activity className="h-4 w-4" /> Market Data Diagnostics (KiteTicker WebSocket)</div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-sm ${diag.market_status === "LIVE" ? "bg-[#00C805]/15 text-[#00C805]" : diag.market_status === "STALE" || diag.market_status === "ERROR" ? "bg-[#FF3B30]/15 text-[#FF6b62]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`} data-testid="market-status">{diag.market_status}</span>
              <button onClick={runRelianceCheck} disabled={relBusy || !zConnected} className="text-[11px] font-semibold px-2.5 py-1 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730] disabled:opacity-50" data-testid="reliance-check-btn">
                {relBusy ? "Checking…" : "Run RELIANCE Check"}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
            {[["Requested", diag.tokens_requested], ["Subscribed", diag.tokens_subscribed], ["With Ticks", diag.tokens_with_ticks],
              ["Ticks Rcvd", diag.tick_count], ["WS Callbacks", diag.callback_count], ["Cache Size", diag.latest_tick_cache_size],
              ["WS Connected", diag.ws_is_connected ? "YES" : "NO"], ["Reconnects", diag.reconnect_count],
              ["Last Tick Age", diag.last_tick_age_seconds != null ? diag.last_tick_age_seconds + "s" : "—"],
              ["Last Msg", diag.last_ws_message_ts ? new Date(diag.last_ws_message_ts).toLocaleTimeString() : "—"],
              ["WS Since", diag.ws_connected_at ? new Date(diag.ws_connected_at).toLocaleTimeString() : "—"],
              ["Last Error", diag.last_ws_error ? JSON.stringify(diag.last_ws_error).slice(0, 20) : "none"]].map(([k, v]) => (
              <div key={k} className="term-panel-2 rounded-sm p-2">
                <div className="text-[10px] text-slate-500 uppercase">{k}</div>
                <div className="mono text-slate-100 text-sm truncate">{String(v)}</div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-600 mt-2">Market Data shows LIVE only when fresh WebSocket ticks have actually arrived. REST is used for bootstrap/diagnostics only, never as the primary feed.</div>

          {relCheck && relCheck.legs && (
            <div className="mt-3 overflow-auto" data-testid="reliance-check-result">
              <table className="w-full text-xs">
                <thead className="bg-[#1A1D24] text-slate-400 text-left">
                  <tr>{["Leg", "Tradingsymbol", "Token", "Expiry", "REST LTP", "WS LTP", "WS Age", "WS Tick?"].map((h) => <th key={h} className="px-2 py-1.5 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {relCheck.legs.map((l) => (
                    <tr key={l.leg} className="gz-row mono">
                      <td className="px-2 py-1.5 capitalize">{l.leg}</td>
                      <td className="px-2 py-1.5 text-fg">{l.tradingsymbol}</td>
                      <td className="px-2 py-1.5">{l.instrument_token}</td>
                      <td className="px-2 py-1.5 text-slate-400">{l.expiry || "—"}</td>
                      <td className="px-2 py-1.5">{l.rest_ltp ?? "—"}</td>
                      <td className="px-2 py-1.5 text-[#5b9bff]">{l.ws_ltp ?? "—"}</td>
                      <td className="px-2 py-1.5 text-slate-400">{l.ws_tick_age != null ? l.ws_tick_age + "s" : "—"}</td>
                      <td className={`px-2 py-1.5 font-bold ${l.has_ws_tick ? "text-[#00C805]" : "text-[#FF6b62]"}`}>{l.has_ws_tick ? "YES" : "NO"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Paper Trading (Zerodha data only — NO synthetic fallback) */}
      {sec === "diagnostics" && paper && futuresOn && (
        <div className="term-panel rounded-sm p-5" data-testid="paper-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Paper Trading · Trade Instances</div>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm ${paper.zerodha_ready ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
              MARKET DATA SOURCE: {paper.market_data_source}
            </span>
          </div>
          {!paper.zerodha_ready && (
            <div className="flex items-center gap-2 text-[11px] text-[#FFCC00] bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm p-2 mb-3">
              <AlertTriangle className="h-4 w-4" /> Connect Zerodha to use Paper Trading with live market data. No synthetic fallback is used in Paper mode.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {paper.trades.map((t) => (
              <div key={t.id} className="term-panel-2 rounded-sm p-3" data-testid={`paper-trade-${t.slot}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm">{t.name}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${t.runtime_status === "WAITING_FOR_ZERODHA" ? "bg-[#FFCC00]/15 text-[#FFCC00]" : "bg-[#00C805]/15 text-[#00C805]"}`}>{t.runtime_status}</span>
                </div>
                <div className="text-[11px] text-slate-500 mono mt-1">{t.strategy_version}</div>
                <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
                  <div><span className="text-slate-500">Env </span><span className="mono text-[#5b9bff]">{t.trading_environment}</span></div>
                  <div><span className="text-slate-500">Exec </span><span className="mono">{t.execution_provider}</span></div>
                  <div><span className="text-slate-500">Alloc </span><span className="mono">{inr(t.allocated_funds)}</span></div>
                  <div><span className="text-slate-500">Lots </span><span className="mono">{t.lots}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
